import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Lock, Settings, ChevronRight, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';

export default function AdminSitemap({ sitemap }) {
  const navigate = useNavigate();

  if (!sitemap) return null;

  return (
    <div data-testid="sitemap-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-fuchsia-600" />Pages Publiques</CardTitle>
            <CardDescription>Structure des pages visibles par tous</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sitemap.pages?.map((page, index) => (
                <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${page.status === 'orphan' ? 'bg-red-50 border border-red-200' : page.status === 'factoriser' ? 'bg-amber-50 border border-amber-200' : 'bg-zinc-50'}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-zinc-900">{page.name}</p>
                      <span className="text-xs px-2 py-0.5 bg-zinc-200 text-zinc-600 rounded-full font-mono">{page.lines} lignes</span>
                      {page.status === 'orphan' && <span className="text-xs px-2 py-0.5 bg-red-200 text-red-700 rounded-full">orpheline</span>}
                      {page.status === 'factoriser' && <span className="text-xs px-2 py-0.5 bg-amber-200 text-amber-700 rounded-full">factoriser</span>}
                    </div>
                    <p className="text-sm text-zinc-500">{page.path}</p>
                    {page.description && <p className="text-xs text-zinc-400 mt-0.5">{page.description}</p>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate(page.path)}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-fuchsia-600" />Pages Administration</CardTitle>
            <CardDescription>Accès réservé au staff</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sitemap.admin_pages?.map((page, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-fuchsia-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-zinc-900">{page.name}</p>
                      <span className="text-xs px-2 py-0.5 bg-fuchsia-200 text-fuchsia-700 rounded-full font-mono">{page.lines} lignes</span>
                    </div>
                    <p className="text-sm text-zinc-500">{page.description}</p>
                    <div className="flex gap-1 mt-1">
                      {page.roles.map(role => (
                        <span key={role} className={`text-xs px-2 py-0.5 rounded-full ${role === 'admin' ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-lime-100 text-lime-700'}`}>{role}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card className="border-0 shadow-lg">
          <CardHeader><CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-fuchsia-600" />Composants</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sitemap.components?.map((comp, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <p className="font-medium text-zinc-900">{comp.name}</p>
                  <span className="text-xs px-2 py-0.5 bg-blue-200 text-blue-700 rounded-full font-mono">{comp.lines} lignes</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader><CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-fuchsia-600" />Backend</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sitemap.backend?.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <p className="font-medium text-zinc-900">{item.name}</p>
                  <span className="text-xs px-2 py-0.5 bg-amber-200 text-amber-700 rounded-full font-mono">{item.lines} lignes</span>
                </div>
              ))}
            </div>
            {sitemap.total_frontend_lines && (
              <div className="mt-4 p-3 bg-zinc-100 rounded-lg text-center">
                <p className="text-sm text-zinc-600"><strong>Total Frontend :</strong> {sitemap.total_frontend_lines.toLocaleString()} lignes &middot; <strong>Backend :</strong> {sitemap.total_backend_lines.toLocaleString()} lignes</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {sitemap.orphan_code && (
          <Card className="border-0 shadow-lg border-l-4 border-l-red-400">
            <CardHeader><CardTitle className="flex items-center gap-2 text-red-700"><Trash2 className="h-5 w-5" />Code Orphelin</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {sitemap.orphan_code.pages?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-zinc-500 uppercase mb-2">Pages</p>
                  {sitemap.orphan_code.pages.map((p, i) => (<div key={i} className="p-2 bg-red-50 rounded-lg mb-1"><p className="text-sm font-medium text-red-800">{p.name} <span className="font-mono text-xs text-red-500">({p.lines} lignes)</span></p><p className="text-xs text-red-600">{p.reason}</p></div>))}
                </div>
              )}
              {sitemap.orphan_code.backend_endpoints?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-zinc-500 uppercase mb-2">Endpoints Backend</p>
                  {sitemap.orphan_code.backend_endpoints.map((e, i) => (<div key={i} className="p-2 bg-red-50 rounded-lg mb-1"><p className="text-sm font-mono text-red-800">{e.endpoint}</p><p className="text-xs text-red-600">{e.reason}</p></div>))}
                </div>
              )}
              {sitemap.orphan_code.frontend_legacy?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-zinc-500 uppercase mb-2">Code Legacy</p>
                  {sitemap.orphan_code.frontend_legacy.map((e, i) => (<div key={i} className="p-2 bg-orange-50 rounded-lg mb-1"><p className="text-sm font-medium text-orange-800">{e.file}</p><p className="text-xs text-orange-600">{e.reason}</p></div>))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {sitemap.factorisation?.length > 0 && (
          <Card className="border-0 shadow-lg border-l-4 border-l-amber-400">
            <CardHeader><CardTitle className="flex items-center gap-2 text-amber-700"><Settings className="h-5 w-5" />Factorisation recommandée</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sitemap.factorisation.map((item, i) => (
                  <div key={i} className="p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-zinc-900">{item.file}</p>
                      <span className="text-xs px-2 py-0.5 bg-amber-200 text-amber-700 rounded-full font-mono">{item.lines} lignes</span>
                    </div>
                    <p className="text-xs text-amber-700">{item.suggestion}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
